import React, { useState, useEffect, useRef } from "react"
import { XMarkIcon, ClipboardIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import MediaLibrary from "aldoo-ra/MediaLibrary"
import ReactDOM from "react-dom"

// Helper function to get YouTube video ID from URL
const getYouTubeVideoID = (url) => {
  if (!url) return null
  const regExp =
    /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/ ]{11})/
  const match = url.match(regExp)
  return match ? match[1] : null
}

const VideoPreview = ({
  isOpen,
  videoUrl,
  fileName,
  onClose,
  onVideoSelect,
  selectedVideoIndex = null,
}) => {
  const [copySuccess, setCopySuccess] = useState({
    filename: false,
    url: false,
  })
  const [windowHeight, setWindowHeight] = useState(window.innerHeight)
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false)
  const [videoResolution, setVideoResolution] = useState(null)
  const [videoSize, setVideoSize] = useState(null)
  const videoRef = useRef(null)

  // Function to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0B"
    const k = 1024
    const sizes = ["B", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + sizes[i]
  }

  // Calculate file size from URL
  const calculateVideoSize = async (url) => {
    if (!url || url.includes("youtube.com") || url.includes("youtu.be")) {
      // For YouTube videos, we don't have direct access to file size
      setVideoSize("Streaming")
      return
    }

    try {
      const response = await fetch(url, { method: "HEAD" })
      if (response.ok) {
        const contentLength = response.headers.get("content-length")
        if (contentLength) {
          const bytes = parseInt(contentLength, 10)
          const formattedSize = formatFileSize(bytes)
          setVideoSize(formattedSize)
        } else {
          setVideoSize("Unknown")
        }
      }
    } catch (error) {
      console.error("Failed to fetch video size:", error)
      setVideoSize("Unknown")
    }
  }

  // Update window height on resize
  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight)
    }

    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  // Get video dimensions and size when URL changes
  useEffect(() => {
    if (videoUrl && isOpen) {
      // Reset values
      setVideoResolution(null)
      setVideoSize(null)

      const isYouTube =
        videoUrl.includes("youtube.com") || videoUrl.includes("youtu.be")

      if (!isYouTube) {
        // For direct video files, get dimensions using a video element
        const video = document.createElement("video")
        video.style.display = "none"

        video.onloadedmetadata = () => {
          setVideoResolution(`${video.videoWidth}x${video.videoHeight}`)
          if (document.body.contains(video)) {
            document.body.removeChild(video)
          }
        }

        video.onerror = () => {
          console.error("Error loading video metadata")
          if (document.body.contains(video)) {
            document.body.removeChild(video)
          }
        }

        document.body.appendChild(video)
        video.src = videoUrl

        // Add safety timeout to clean up if metadata doesn't load
        const timeout = setTimeout(() => {
          if (document.body.contains(video)) {
            document.body.removeChild(video)
          }
        }, 5000)

        calculateVideoSize(videoUrl)

        return () => {
          clearTimeout(timeout)
          if (document.body.contains(video)) {
            document.body.removeChild(video)
          }
        }
      } else {
        // For YouTube videos, set resolution to a standard format
        // Actual resolution depends on what YouTube provides
        setVideoResolution("HD") // YouTube typically streams in HD
        setVideoSize("Streaming")
      }
    }
  }, [videoUrl, isOpen])

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose()
      }
    }
    document.addEventListener("keydown", handleEscape)
    return () => {
      document.removeEventListener("keydown", handleEscape)
    }
  }, [isOpen, onClose])

  const handleCopyFileName = async () => {
    try {
      await navigator.clipboard.writeText(fileName)
      setCopySuccess((prev) => ({ ...prev, filename: true }))
      setTimeout(
        () => setCopySuccess((prev) => ({ ...prev, filename: false })),
        2000
      )
    } catch (err) {
      console.error("Failed to copy filename:", err)
    }
  }

  const handleCopyVideoUrl = async () => {
    try {
      await navigator.clipboard.writeText(videoUrl)
      setCopySuccess((prev) => ({ ...prev, url: true }))
      setTimeout(
        () => setCopySuccess((prev) => ({ ...prev, url: false })),
        2000
      )
    } catch (err) {
      console.error("Failed to copy video URL:", err)
    }
  }

  // Media Library handlers
  const handleMediaLibraryOpen = (e) => {
    e.stopPropagation()
    setIsMediaLibraryOpen(true)
  }

  const handleFileSelect = (file) => {
    // Handle the selected file from Media Library
    if (onVideoSelect) {
      // Here's the key fix - we're passing the file with our stored selectedVideoIndex
      // and a flag to indicate it's coming from the preview
      const fileWithIndex = {
        ...file,
        selectedIndex: selectedVideoIndex,
        fromPreview: true,
      }
      onVideoSelect(fileWithIndex)

      // Just close the media library, not the preview
      setIsMediaLibraryOpen(false)
    }
  }

  if (!isOpen || !videoUrl) return null

  const isYouTubeVideo =
    (videoUrl?.includes("youtube.com") || videoUrl?.includes("youtu.be")) &&
    getYouTubeVideoID(videoUrl)

  // Calculate available height for video container (subtract top bar height)
  const topBarHeight = 64 // Approximate height of the top bar in pixels
  const availableHeight = windowHeight - topBarHeight

  // Create the preview content
  const previewContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex flex-col z-50"
      style={{
        zIndex: 1000,
        margin: 0,
        padding: 0,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}
    >
      {/* Top control bar */}
      <div
        className="w-full bg-black bg-opacity-70 p-4 flex items-center gap-4 flex-shrink-0"
        onClick={(e) => e.stopPropagation()}
        style={{ margin: 0 }}
      >
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopyFileName}
              className="p-2 rounded-full hover:bg-admin_neutral_3 transition-colors duration-200 text-white"
              aria-label="Copy filename"
            >
              <ClipboardIcon className="w-5 h-5" />
            </button>
            <span className="text-white text-sm truncate max-w-xs">
              {copySuccess.filename ? "Copied!" : fileName}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopyVideoUrl}
              className="p-2 rounded-full hover:bg-admin_neutral_3 transition-colors duration-200 text-white"
              aria-label="Copy video URL"
            >
              <ClipboardIcon className="w-5 h-5" />
            </button>
            <span className="text-white text-sm">
              {copySuccess.url ? "Copied!" : "Video URL"}
            </span>
          </div>

          {/* Add resolution chip */}
          {videoResolution && (
            <div className="text-white text-sm bg-gray-800 px-3 py-1 rounded-full border border-admin_bordered">
              Resolution: {videoResolution}
            </div>
          )}

          {/* Add size chip */}
          {videoSize && (
            <div className="text-white text-sm bg-gray-800 px-3 py-1 rounded-full border border-admin_bordered">
              Size: {videoSize}
            </div>
          )}
        </div>

        {/* Add Media Library button */}
        <div className="flex-1 flex justify-end items-center gap-4">
          <Button
            variant="admin_secondary"
            className="w-[150px] hover:bg-admin_neutral_3"
            onClick={handleMediaLibraryOpen}
          >
            Media Library
          </Button>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-admin_neutral_3 transition-colors duration-200"
            aria-label="Close preview"
          >
            <XMarkIcon className="w-6 h-6 text-white" />
          </button>
        </div>
      </div>

      {/* Video container - using calculated available height */}
      <div
        className="flex items-center justify-center p-4 overflow-hidden"
        style={{
          height: availableHeight,
          margin: 0,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative w-full h-full flex items-center justify-center">
          {isYouTubeVideo ? (
            <iframe
              width="1280"
              height="720"
              src={`https://www.youtube.com/embed/${getYouTubeVideoID(
                videoUrl
              )}`}
              title={fileName || "YouTube Video"}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="max-w-full max-h-full rounded-lg shadow-lg"
              style={{ maxHeight: `${availableHeight - 32}px`, margin: 0 }}
            />
          ) : (
            <video
              controls
              src={videoUrl}
              className="max-w-full max-h-full rounded-lg shadow-lg"
              style={{ maxHeight: `${availableHeight - 32}px`, margin: 0 }}
              autoPlay
              ref={videoRef}
            >
              Your browser does not support the video tag.
            </video>
          )}
        </div>
      </div>

      {/* Media Library Modal */}
      <MediaLibrary
        isOpen={isMediaLibraryOpen}
        onClose={() => setIsMediaLibraryOpen(false)}
        onFileSelect={handleFileSelect}
        title="Replace Video"
        defaultFileType="video"
      />
    </div>
  )

  // Use createPortal to render directly to document.body
  return ReactDOM.createPortal(previewContent, document.body)
}

export default VideoPreview
